import React, { ChangeEvent, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { UserActions } from 'store/User/Actions';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';
import { extraSmsToBuyOptions, extraTokenToBuyOptions } from 'utils/options';
import { OrganizationRole } from 'utils/types/BrokerModels';
import { AdvertValidatorType, isFieldValid } from 'utils/validators/advertAddingValidator';
import { v4 } from 'uuid';

import Divider from 'components/common/Divider/Divider';
import { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import AutocompleteInput from 'components/common/Inputs/AutocompleteInput/AutocompleteInput';
import CheckboxTextInput from 'components/common/Inputs/CheckboxTextInput.tsx';
import TextInput from 'components/common/Inputs/TextInput';
import TextBox, { TextBoxStructureType } from 'components/common/TextBox';
import { activeNotification } from 'components/functions/activeNotification';
import { Title } from 'components/HeaderSection';
import { FormRadio } from 'components/InputFilters';

import { SaveButton } from './SaveButton';

const NotificationsContainer = styled.div`
    padding: 24px 10px 32px 20px;
    border-radius: var(--box-border-radius);
    background: var(--color-white);
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 40px;
    margin-bottom: 60px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ToggleInpuContainer = styled.div`
    margin-top: 45px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const NotificationsInputContainer = styled.div`
    padding-bottom: 10px;
`;

const AutocompleteWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 50%;
    padding: 15px 15px 0 0;
    margin-bottom: 20px;

    @media (width < 991px) {
        width: 100%;
    }
`;

const InputAndButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--color-alt-second);
    color: var(--color-primary);
    border-radius: var(--border-radius);
`;

const Button = styled.div`
    background: var(--color-alt-second);
    border-radius: 40px;
    height: 50px;
    border: none;
    padding: 10px;
    display: flex;
    align-items: center;
    width: 35%;
    margin-left: auto;
    justify-content: center;
    text-decoration: none !important;
    cursor: pointer;

    span {
        font-size: var(--font-size-body-2);
        text-align: center;
        width: 100%;
        padding: 5px;
        border-radius: 40px;
        color: white;
        background-color: var(--color-alt);
    }
`;

interface NotificationsTabProps {
    title: string;
}

const NotificationsTab = forwardRef<HTMLDivElement, NotificationsTabProps>(({ title }, ref: ForwardedRef<HTMLDivElement>) => {
    const { user, dispatch } = useUser();
    const [isEditableInput, setIsEditableInput] = useState(false);
    const [triggerValidation, setTriggerValidation] = useState(false);
    const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
    const navigate = useNavigate();
    const platform = usePlatform();

    const [userNotificationData, setUserNotificationData] = useState(user.notificationSettings);

    useEffect(() => {
        UserActions.getNotificationSettings(dispatch);
        UserActions.getTokenInfo(dispatch);
    }, [dispatch]);

    useEffect(() => {
        setUserNotificationData(user.notificationSettings);
        isBrowserNotificationsEnabled();
    }, [user.notificationSettings]);

    const handleUserDataChange = (field: keyof typeof userNotificationData, value: string) => {
        setUserNotificationData((prevData) => ({
            ...prevData,
            [field]: value
        }));
    };

    const [smsToBuy, setSmsToBuy] = useState<string>('100');
    const [tokenToBuy, setTokenToBuy] = useState<string>('100000');
    const [editedDailyLimit, setEditedDailyLimit] = useState<string | number | undefined>(userNotificationData.smsDayLimit?.toString());

    const handleSmsToBuyChange = (value: string) => {
        setSmsToBuy(value);
    };

    const handleTokenToBuyChange = (value: string) => {
        setTokenToBuy(value);
    };

    const handleSubmitBuySms = async () => {
        try {
            const selectedSmsCount = parseInt(smsToBuy);

            if (selectedSmsCount < 100 || selectedSmsCount > 800) {
                activeNotification('Błąd', 'Nieprawidłowa ilość SMS, wybierz ilość sms ', 'danger');

                return;
            }

            navigate('/purchase/sms', {
                state: { quantity: selectedSmsCount }
            });
        } catch (error) {
            const errorId = v4();

            if (error instanceof Error) {
                userApiRequests.logException(error, errorId);
            }
            activeNotification('Błąd', 'Nie udało się przejść do zakupu SMS', 'danger');
        }
    };

    const handleSubmitBuyToken = async () => {
        try {
            const selectedTokenCount = parseInt(tokenToBuy);

            if (selectedTokenCount < 100000 || selectedTokenCount > 800000) {
                activeNotification('Błąd', 'Nieprawidłowa ilość TOKENÓW, wybierz ilość TOKENÓW ', 'danger');

                return;
            }

            navigate('/purchase/tokens', {
                state: { quantity: selectedTokenCount }
            });
        } catch (error) {
            const errorId = v4();

            if (error instanceof Error) {
                userApiRequests.logException(error, errorId);
            }
            activeNotification('Błąd', 'Nie udało się przejść do zakupu TOKENÓW', 'danger');
        }
    };

    const handleAlertNotificationsChange = () => {
        userApiRequests.changeSmsAndMailNotificationStatus(!userNotificationData.activeNotification);
        setUserNotificationData((prev) => ({
            ...prev,
            activeNotification: !userNotificationData.activeNotification
        }));
    };

    const handlePushNotificationsChange = () => {
        userApiRequests.changePushNotificationStatus(!userNotificationData.pushNotification);
        setUserNotificationData((prev) => ({
            ...prev,
            pushNotification: !userNotificationData.pushNotification
        }));
    };

    const handleDailyLimitChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
        setEditedDailyLimit(value);
    };

    const handleLimitChangeButton = async () => {
        if (isEditableInput) {
            if (editedDailyLimit !== '') {
                await userApiRequests.changeDayLimit(Number(editedDailyLimit));
                await UserActions.getNotificationSettings(dispatch);
                setIsEditableInput(false);
            }
        } else {
            setIsEditableInput(!isEditableInput);
            setEditedDailyLimit(userNotificationData.smsDayLimit);
        }
        setIsEditableInput(!isEditableInput);
    };

    const onOutsideClick = () => {
        setIsEditableInput(false);
    };

    const handleSave = () => {
        setTriggerValidation(true);

        if (isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, userNotificationData.notificationEmail) &&
            isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, userNotificationData.notificationPhone)) {
            setTriggerValidation(false);
            userApiRequests.changeNotificationMailAndPhoneNumber(userNotificationData.notificationEmail, userNotificationData.notificationPhone);
        }
    };

    const isIos = () => {
        const userAgent = window.navigator.userAgent.toLowerCase();

        return (/iphone|ipad|ipod/).test(userAgent);
    };

    const isSupported = () => 'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window;

    const handleChangeBrowserNotifications = () => {
        if (isIos() || !isSupported()) {
            return;
        }

        const isNotificationPermissionGranted = isBrowserNotificationsEnabled();

        if (!isNotificationPermissionGranted) {
            Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                    setIsNotificationEnabled(true);
                } else {
                    setIsNotificationEnabled(false);
                }
            });
        }
    };

    const isBrowserNotificationsEnabled = () => {
        if (isIos() || !isSupported()) return;

        const notificationPermission = Notification.permission === 'granted';
        setIsNotificationEnabled(notificationPermission);

        return notificationPermission;
    };

    const notificationSettingsStruture = [
        {
            title: 'Powiadomienia E-mail i SMS',
            icon: IconEnum.AUDIO,
            helperText:
            'Jeśli wyłączysz powiadomienia nie będziesz otrzymywać powiadomień z żadnego stworzonego przez siebie filtra, na email i telefon.',
            formRadio:
                <FormRadio
                    name="Wyłączyć powiadomienia?"
                    value={userNotificationData.activeNotification}
                    onChange={handleAlertNotificationsChange}
                    optionsText={{ trueOptionText: 'Włączone', falseOptionText: 'Wyłączone' }}
                />

        },
        {
            title: 'Powiadomienia PUSH',
            icon: IconEnum.INFO,
            formRadio:
                <FormRadio
                    name="Wyłączyć powiadomienia?"
                    value={userNotificationData.pushNotification}
                    onChange={handlePushNotificationsChange}
                    optionsText={{ trueOptionText: 'Włączone', falseOptionText: 'Wyłączone' }}
                />

        },
        ...!isIos()
            ? [
                {
                    title: 'Powiadomienia PUSH w przeglądarce',
                    icon: IconEnum.INFO,
                    formRadio:
                <CheckboxTextInput label={'Włączyć powiadomienia?'} value={isNotificationEnabled} onChange={handleChangeBrowserNotifications} />
                }
            ]
            : []
    ];

    const textBoxesSmsStructure: TextBoxStructureType[] = [
        {
            title: 'Limity Powiadomień SMS',
            titleIcon: IconEnum.CHART,
            label: 'Wykorzystano/Pakiet SMS',
            value: `${userNotificationData.totalMonthSmsToUse}`,
            tooltipText: <p>
                {`Dostępna liczba SMS z pakietu: ${user.notificationSettings.smsPackage}`}<br/>
                {`Dostępna liczba SMS dokupionych: ${user.notificationSettings.boughtSms}`}
            </p>
        },
        {
            title: '',
            label: 'Dzienny limit wiadomości SMS:',
            value: userNotificationData.smsDayLimit,
            editedValue: editedDailyLimit,
            actions: {
                actionText: isEditableInput ? 'Zapisz zmiany' : 'Zmień limit',
                actionClick: handleLimitChangeButton,
                isEditable: isEditableInput,
                onChange: handleDailyLimitChange,
                onOutsideClick

            }
        }
    ];

    const textBoxesTokenStructure: TextBoxStructureType[] = [
        {
            title: 'Limity Tokenów AI',
            titleIcon: IconEnum.CHART,
            label: 'Pozostało/Pakiet TOKENY',
            value: `${user.tokenInfo.monthAiTokensToUse}`,
            tooltipText: <p>
                {`Dostepne TOKENY z pakietu: ${user.tokenInfo.monthAiTokensToUse}`}<br/>
                {`Dokupione dostępne TOKENY: ${user.tokenInfo.boughtAiToken}`}
            </p>
        }
    ];

    return (
        <div ref={ref}>
            <Title>{title}</Title>
            <NotificationsContainer>
                <Wrapper>
                    <NotificationsInputContainer>
                        <InputContainer title="Dane do powiadomień" icon={IconEnum.ALERT}>
                            <TextInput
                                value={userNotificationData.notificationEmail}
                                onChange={(e) => handleUserDataChange('notificationEmail', e.target.value)}
                                placeholder={userNotificationData.notificationEmail}
                                type="email"
                                icon={IconEnum.EMAIL}
                                inputValidation={(value: string | undefined | null | number) => !isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, value as string)}
                                errorMessage={'Pole nie może być puste'}
                                triggerValidation={triggerValidation}
                            />
                        </InputContainer>
                        <InputContainer title="">
                            <TextInput
                                value={userNotificationData.notificationPhone}
                                onChange={(e) => handleUserDataChange('notificationPhone', e.target.value)}
                                placeholder={userNotificationData.notificationPhone}
                                type="tel"
                                icon={IconEnum.PHONE}
                                inputValidation={(value: string | undefined | null | number) => !isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, value as string)}
                                errorMessage={'Pole nie może być puste'}
                                triggerValidation={triggerValidation}

                            />
                        </InputContainer>
                    </NotificationsInputContainer>
                    <SaveButton onClick={handleSave}>Zapisz zmiany</SaveButton>
                    <ToggleInpuContainer>
                        {notificationSettingsStruture.map((setting, index) => <InputContainer
                            key={index}
                            title={setting.title}
                            icon={setting.icon}
                            visibleHelperText={setting.helperText}
                        >
                            {setting.formRadio}
                        </InputContainer>)}
                    </ToggleInpuContainer>
                </Wrapper>

                <Wrapper>
                    {textBoxesSmsStructure.map((el) => {
                        return (
                            <TextBox
                                key={el.label}
                                label={el.label}
                                title={el.title}
                                icon={el.titleIcon}
                                labelIcon={el.labelIcon}
                                actionText={el.actions?.actionText}
                                editedValue={el.editedValue}
                                onActionClick={el.actions?.actionClick}
                                isEditMode={el.actions?.isEditable}
                                value={el.value}
                                onChange={el.actions?.onChange}
                                onOutsideClick={el.actions?.onOutsideClick}
                                tooltipText={el.tooltipText}>
                            </TextBox>
                        );
                    })}
                    {/* Jeśli user nie należy do organizacji Api zwraca organizationRole jako '' */}
                    {(user.organizationRole === OrganizationRole.ORGANIZATION_ADMIN || user.organizationRole === '') && platform !== Platform.IOS &&
                      <AutocompleteWrapper>
                          <InputContainer title={'Dokup pakiet SMS:'}
                              icon={IconEnum.LABEL}
                              visibleHelperText={'Cena 100 wiadomości SMS wynosi 25,00zł'}>
                              <InputAndButtonWrapper>
                                  <AutocompleteInput
                                      noAction
                                      noTyping
                                      placeholder={'Wybierz ilość sms...'}
                                      value={smsToBuy}
                                      options={extraSmsToBuyOptions}
                                      // @ts-expect-error value is only string here
                                      onChange={handleSmsToBuyChange} />
                                  <Button darkMode={user.isDarkMode} onClick={handleSubmitBuySms}><span>Kup</span></Button>
                              </InputAndButtonWrapper>
                          </InputContainer>
                      </AutocompleteWrapper>
                    }

                    {textBoxesTokenStructure.map((el) => {
                        return (
                            <TextBox
                                key={el.label}
                                label={el.label}
                                title={el.title}
                                icon={el.titleIcon}
                                labelIcon={el.labelIcon}
                                actionText={el.actions?.actionText}
                                editedValue={el.editedValue}
                                onActionClick={el.actions?.actionClick}
                                isEditMode={el.actions?.isEditable}
                                value={el.value}
                                isInfoBox
                                onChange={el.actions?.onChange}
                                onOutsideClick={el.actions?.onOutsideClick}
                                tooltipText={el.tooltipText}>
                            </TextBox>
                        );
                    })}
                    {/* Jeśli user nie należy do organizacji Api zwraca organizationRole jako '' */}
                    {(user.organizationRole === OrganizationRole.ORGANIZATION_ADMIN || user.organizationRole === '') && platform !== Platform.IOS &&
                      <AutocompleteWrapper>
                          <InputContainer title={'Dokup pakiet TOKENÓW:'}
                              icon={IconEnum.LABEL}
                              visibleHelperText={'Cena 100 000 Tokenów wynosi 12,50zł'}>
                              <InputAndButtonWrapper>
                                  <AutocompleteInput
                                      noAction
                                      noTyping
                                      placeholder={'Wybierz ilość tokenów...'}
                                      value={tokenToBuy}
                                      options={extraTokenToBuyOptions}
                                      // @ts-expect-error value is only string here
                                      onChange={handleTokenToBuyChange} />
                                  <Button darkMode={user.isDarkMode} onClick={handleSubmitBuyToken}><span>Kup</span></Button>
                              </InputAndButtonWrapper>
                          </InputContainer>
                      </AutocompleteWrapper>
                    }

                    <Divider />
                    <TextBox label={'Data zresetowania pakietów SMS i TOKEN'} value={dayjs(userNotificationData.smsDayReset).format('DD.MM.YYYY')}/>
                </Wrapper>

            </NotificationsContainer>
        </div>
    );
});

export default NotificationsTab;
